import { useEffect, useState } from "react";
import { useSubdomain } from "./SubdomainProvider.js";
import AdminLogin, { adminLoginPayload } from "./AdminLogin.js";
import AdminNavigation from "./AdminNavigation.js";
import { notifyActorRolesChange } from "../config/makeWebhooks.js";
import { actorDisplayName, fetchActors } from "../config/actorAssets.js";

const API_URL = process.env.REACT_APP_API_URL;

function Settings() {
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [passwordInput, setPasswordInput] = useState("");
  const [settings, setSettings] = useState(null);
  const [actors, setActors] = useState([]);
  const [error, setError] = useState("");
  const subdomain = useSubdomain();

  const isLoggedIn = !!token;

  const actorRolesKey =
    subdomain === "seattle" ? "actorRolesSeattle" : "actorRolesApp";
  const activeActorsKey =
    subdomain === "seattle" ? "activeActorsSeattle" : "activeActorsApp";

  const allActors = actors.map((a) => a.key);
  const actorLabelByKey = Object.fromEntries(
    actors.map((a) => [a.key, actorDisplayName(a)])
  );

  const normalizeSettings = (data, actorKeys) => {
    if (!data[actorRolesKey] || typeof data[actorRolesKey] !== "object") {
      data[actorRolesKey] = {};
    }

    const indexMap = new Map(actorKeys.map((a, i) => [a, i]));
    const normalizeUniqueRole = (roleName) => {
      const rolesObj = data[actorRolesKey] || {};
      const actorsWithRole = Object.keys(rolesObj).filter(
        (a) => rolesObj[a] === roleName
      );
      if (actorsWithRole.length <= 1) return;

      actorsWithRole.sort(
        (a, b) =>
          (indexMap.get(a) ?? Number.MAX_SAFE_INTEGER) -
          (indexMap.get(b) ?? Number.MAX_SAFE_INTEGER)
      );

      actorsWithRole.slice(1).forEach((a) => {
        delete rolesObj[a];
      });

      data[actorRolesKey] = rolesObj;
    };

    normalizeUniqueRole("Marble");
    normalizeUniqueRole("Handler");

    data[activeActorsKey] = Object.keys(data[actorRolesKey] || {}).filter(
      (actor) =>
        data[actorRolesKey][actor] && data[actorRolesKey][actor] !== "Off"
    );

    const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
    if (!data[wardrobeKey]) {
      data[wardrobeKey] = "Jeans";
    }

    if (typeof data.specialEventSeattle !== "boolean") {
      data.specialEventSeattle =
        typeof data.specialEvent === "boolean" ? data.specialEvent : false;
    }
    if (typeof data.specialEventApp !== "boolean") {
      data.specialEventApp =
        typeof data.specialEvent === "boolean" ? data.specialEvent : false;
    }
    delete data.specialEvent;

    return data;
  };

  const handleLogin = () => {
    fetch(`${API_URL}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: adminLoginPayload(passwordInput, subdomain),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Login failed");
        return res.json();
      })
      .then((data) => {
        localStorage.setItem("adminToken", data.token);
        setToken(data.token);
        setError("");
      })
      .catch(() => {
        setError("Incorrect password");
      });
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    Promise.all([
      fetch(`${API_URL}/api/settings`).then((res) => res.json()),
      fetchActors(API_URL, subdomain),
    ])
      .then(([settingsData, actorsData]) => {
        setActors(actorsData);
        const keys = actorsData.map((a) => a.key);

        const next = { ...settingsData };
        if (!next[actorRolesKey] || typeof next[actorRolesKey] !== "object") {
          next[actorRolesKey] = {};
        }

        const indexMap = new Map(keys.map((a, i) => [a, i]));
        const normalizeUniqueRole = (roleName) => {
          const rolesObj = next[actorRolesKey] || {};
          const actorsWithRole = Object.keys(rolesObj).filter(
            (a) => rolesObj[a] === roleName
          );
          if (actorsWithRole.length <= 1) return;

          actorsWithRole.sort(
            (a, b) =>
              (indexMap.get(a) ?? Number.MAX_SAFE_INTEGER) -
              (indexMap.get(b) ?? Number.MAX_SAFE_INTEGER)
          );

          actorsWithRole.slice(1).forEach((a) => {
            delete rolesObj[a];
          });

          next[actorRolesKey] = rolesObj;
        };

        normalizeUniqueRole("Marble");
        normalizeUniqueRole("Handler");

        next[activeActorsKey] = Object.keys(next[actorRolesKey] || {}).filter(
          (actor) =>
            next[actorRolesKey][actor] && next[actorRolesKey][actor] !== "Off"
        );

        const wardrobeKey =
          subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
        if (!next[wardrobeKey]) {
          next[wardrobeKey] = "Jeans";
        }

        if (typeof next.specialEventSeattle !== "boolean") {
          next.specialEventSeattle =
            typeof next.specialEvent === "boolean" ? next.specialEvent : false;
        }
        if (typeof next.specialEventApp !== "boolean") {
          next.specialEventApp =
            typeof next.specialEvent === "boolean" ? next.specialEvent : false;
        }
        delete next.specialEvent;

        setSettings(next);
      })
      .catch(() => setError("Failed to load settings"));
  }, [isLoggedIn, subdomain, actorRolesKey, activeActorsKey]);

  const setActorRole = (actor, role) => {
    if (!settings) return;

    const actorRoles = settings[actorRolesKey] || {};
    const newActorRoles = { ...actorRoles };

    if (role === "Off" || role === null || role === undefined) {
      delete newActorRoles[actor];
    } else {
      Object.keys(newActorRoles).forEach((a) => {
        if (a !== actor && newActorRoles[a] === role) {
          delete newActorRoles[a];
        }
      });
      newActorRoles[actor] = role;
    }

    const newActiveActors = Object.keys(newActorRoles).filter(
      (a) => newActorRoles[a] && newActorRoles[a] !== "Off"
    );

    setSettings({
      ...settings,
      [actorRolesKey]: newActorRoles,
      [activeActorsKey]: newActiveActors,
    });
  };

  const handleSave = () => {
    fetch(`${API_URL}/api/settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(settings),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Save failed");
        if (res.ok) alert("Save successful");
        return res.json();
      })
      .then(() => {
        notifyActorRolesChange(settings[actorRolesKey] || {}, subdomain, token).catch(
          (err) => console.error("Actor roles webhook failed:", err)
        );
      })
      .then(() =>
        fetch(`${API_URL}/api/settings`)
          .then((res) => res.json())
          .then((updatedData) => {
            setSettings(normalizeSettings(updatedData, allActors));
          })
      )
      .catch((err) => alert("Failed to save: " + err.message));
  };

  if (!isLoggedIn) {
    return (
      <AdminLogin
        pageTitle="Settings"
        passwordInput={passwordInput}
        setPasswordInput={setPasswordInput}
        onLogin={handleLogin}
        error={error}
      />
    );
  }

  if (!settings) return <p>Loading settings...</p>;

  return (
    <div
      style={{ display: "inline-block", fontSize: "5vw", paddingLeft: "10vw" }}
    >
      <title>SETTINGS | Spies Among Us</title>
      <h1 style={{ fontSize: "8vw", marginBottom: "2vw", color: "#F9DF39" }}>
        {subdomain === "seattle" ? "SEATTLE" : "LOS ANGELES"}
      </h1>
      <h1>Admin Panel</h1>
      <div>
        <h2>Actor Roles ({subdomain}):</h2>
        {allActors.length === 0 && (
          <p style={{ fontSize: "4vw" }}>
            No actors yet. Create some on the Actors page.
          </p>
        )}
        {allActors.map((actor) => {
          const currentRole = settings[actorRolesKey]?.[actor] || "Off";
          return (
            <div
              key={actor}
              style={{
                display: "block",
                fontSize: "8vw",
                padding: "1vw",
                marginBottom: "2vw",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "0.5vw" }}>
                {actorLabelByKey[actor] || actor}:
              </div>
              <label
                style={{
                  display: "inline-block",
                  fontSize: "8vw",
                  padding: "0.5vw",
                  marginRight: "2vw",
                }}
              >
                <input
                  type="radio"
                  name={`actor-${actor}`}
                  value="Marble"
                  checked={currentRole === "Marble"}
                  onChange={() => setActorRole(actor, "Marble")}
                  style={{ width: "8vw", height: "8vw", marginRight: "1vw" }}
                />
                Marble
              </label>
              <label
                style={{
                  display: "inline-block",
                  fontSize: "8vw",
                  padding: "0.5vw",
                  marginRight: "2vw",
                }}
              >
                <input
                  type="radio"
                  name={`actor-${actor}`}
                  value="Handler"
                  checked={currentRole === "Handler"}
                  onChange={() => setActorRole(actor, "Handler")}
                  style={{ width: "8vw", height: "8vw", marginRight: "1vw" }}
                />
                Handler
              </label>
              <label
                style={{
                  display: "inline-block",
                  fontSize: "8vw",
                  padding: "0.5vw",
                }}
              >
                <input
                  type="radio"
                  name={`actor-${actor}`}
                  value="Off"
                  checked={currentRole === "Off" || !currentRole}
                  onChange={() => setActorRole(actor, "Off")}
                  style={{ width: "8vw", height: "8vw", marginRight: "1vw" }}
                />
                Off
              </label>
            </div>
          );
        })}
      </div>
      <div>
        <h2>Wardrobe ({subdomain}):</h2>
        <div>
          <label style={{ display: "block", fontSize: "10vw", padding: "1vw" }}>
            <input
              type="radio"
              name="wardrobe"
              value="Jeans"
              checked={(subdomain === "seattle" ? settings.seattleWardrobe : settings.appWardrobe) === "Jeans"}
              onChange={() => {
                const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
                const updatedSettings = {
                  ...settings,
                  [wardrobeKey]: "Jeans",
                };
                setSettings(updatedSettings);
              }}
              style={{ width: "10vw", height: "10vw" }}
            />
            Jeans
          </label>
          <label style={{ display: "block", fontSize: "10vw", padding: "1vw" }}>
            <input
              type="radio"
              name="wardrobe"
              value="Shorts"
              checked={(subdomain === "seattle" ? settings.seattleWardrobe : settings.appWardrobe) === "Shorts"}
              onChange={() => {
                const wardrobeKey = subdomain === "seattle" ? "seattleWardrobe" : "appWardrobe";
                const updatedSettings = {
                  ...settings,
                  [wardrobeKey]: "Shorts",
                };
                setSettings(updatedSettings);
              }}
              style={{ width: "10vw", height: "10vw" }}
            />
            Shorts
          </label>
        </div>
      </div>
      <div>
        <h2>Special Event ({subdomain}):</h2>
        <label style={{ display: "block", fontSize: "10vw", padding: "1vw" }}>
          <input
            type="checkbox"
            checked={
              !!settings[
                subdomain === "seattle"
                  ? "specialEventSeattle"
                  : "specialEventApp"
              ]
            }
            onChange={(e) => {
              const specialEventKey =
                subdomain === "seattle"
                  ? "specialEventSeattle"
                  : "specialEventApp";
              setSettings({
                ...settings,
                [specialEventKey]: e.target.checked,
              });
            }}
          />
          <span
            className="checkbox"
            style={{
              width: "10vw",
              height: "10vw",
              minWidth: "10vw",
              minHeight: "10vw",
              verticalAlign: "middle",
              top: 0,
              marginRight: "2vw",
            }}
          />
          Special Event
        </label>
      </div>
      {settings.lastUpdated && (
        <div style={{ marginTop: "2vw", fontSize: "4vw", color: "#888" }}>
          <strong>Last Updated:</strong> {settings.lastUpdated}
        </div>
      )}
      <br />
      <button onClick={handleSave} className="settingsPageButton">
        Save Changes
      </button>
      <AdminNavigation currentPage="settings" />
    </div>
  );
}

export default Settings;
